package fyi.moko.hotpl.ui.photo

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.calculateCentroid
import androidx.compose.foundation.gestures.calculatePan
import androidx.compose.foundation.gestures.calculateZoom
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import fyi.moko.hotpl.ui.components.PhotoLoad
import fyi.moko.hotpl.ui.components.rememberStoredPhoto
import kotlinx.coroutines.delay
import org.koin.compose.viewmodel.koinViewModel

/**
 * The full-screen photo viewer: the place's photos (or one visit's), at Fit by default,
 * pinch to zoom, drag to pan.
 *
 * 리뷰어가 요청한 세 가지(확대·이동·잘리지 않기)는 기능 셋이 아니라 **없는 화면 하나**였다
 * (2026-09-08 피드백 R-2·R-3·R-9). 헤더는 여전히 `Crop`으로 가게를 식별하는 자리이고,
 * 사진을 들여다보는 일은 전부 여기서 한다.
 *
 * 이 화면이 오버레이가 아니라 내비게이션 목적지인 이유는 뒤로 가기 때문이다 — 오버레이면
 * Android 시스템 백이 상세 화면을 통째로 닫고, iOS는 nav host에 깔린 엣지 스와이프가
 * 확대 중의 팬을 화면 뒤로가기로 가져간다. 설계 근거는 `docs/photo-viewer-step3-design.md`.
 */
@Composable
fun PhotoViewerScreen(
    placeId: String,
    visitId: String?,
    fileName: String?,
    onClose: () -> Unit,
) {
    val viewModel: PhotoViewerViewModel = koinViewModel()
    LaunchedEffect(placeId, visitId) {
        val source = if (visitId.isNullOrBlank()) {
            PhotoViewerSource.PlacePhotos(placeId)
        } else {
            PhotoViewerSource.VisitPhotos(placeId, visitId)
        }
        viewModel.start(source, fileName)
    }
    // 마지막 장을 지우면 볼 것이 없다 — 빈 검정 화면을 남기지 않고 상세로 돌아간다.
    LaunchedEffect(viewModel.closed) { if (viewModel.closed) onClose() }

    val pages = viewModel.pages
    Box(Modifier.fillMaxSize().background(Color.Black)) {
        if (pages.isNotEmpty()) {
            val pagerState = rememberPagerState(initialPage = viewModel.index) { pages.size }
            LaunchedEffect(pagerState) {
                snapshotFlow { pagerState.currentPage }.collect(viewModel::onPageChanged)
            }
            var zoomed by remember { mutableStateOf(false) }

            HorizontalPager(
                state = pagerState,
                modifier = Modifier.fillMaxSize(),
                // 확대 중에는 pager가 수평 드래그를 가져가지 않는다. 이걸 켠 채로 두면
                // 팬과 장 넘기기가 같은 손짓을 두고 싸운다.
                userScrollEnabled = !zoomed,
            ) { page ->
                ZoomablePhoto(
                    fileName = pages[page],
                    onZoomChanged = { if (page == pagerState.currentPage) zoomed = it },
                )
            }
        }

        ViewerChrome(
            position = if (pages.isEmpty()) "" else "${viewModel.index + 1} / ${pages.size}",
            canRotate = pages.isNotEmpty(),
            rotating = viewModel.rotating,
            onClose = onClose,
            onRotate = viewModel::rotateCurrent,
            onOpenActions = viewModel::openActions,
        )

        viewModel.message?.let { text ->
            // 스낵바 호스트를 세우지 않는다 — 이 화면은 검은 전면이고, 실패는 드물며, 할 수
            // 있는 일은 "다시 눌러 보기"뿐이라 동작 버튼이 붙을 자리가 없다.
            LaunchedEffect(text) {
                delay(3_000)
                viewModel.clearMessage()
            }
            Text(
                text = text,
                style = MaterialTheme.typography.bodyMedium,
                color = Color.White,
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .safeDrawingPadding()
                    .padding(24.dp)
                    .background(Color.Black.copy(alpha = 0.7f), MaterialTheme.shapes.small)
                    .padding(horizontal = 16.dp, vertical = 10.dp),
            )
        }
    }

    if (viewModel.actionsOpen) {
        PhotoActionSheet(
            actions = buildList {
                if (viewModel.canSetAsCover) {
                    add(PhotoAction("Make this the cover", viewModel::setAsCover))
                }
                if (viewModel.canPromoteToPlace) {
                    add(PhotoAction("Move to the place's photos", viewModel::promoteToPlace))
                }
                add(PhotoAction("Delete this photo", viewModel::removeCurrent))
            },
            onDismiss = viewModel::dismissActions,
        )
    }
}

/**
 * 사진 한 장. 배율과 위치는 [ZoomPan]이 계산하고 여기서는 제스처를 받아 넘기기만 한다.
 * 상태는 파일명으로 기억되므로 장을 넘기면 1×로 돌아온다 — 확대한 채로 다음 장에 들어가면
 * 그 사진이 잘려 보이고, 그것이 바로 이 화면이 고치려는 증상이다.
 */
@Composable
private fun ZoomablePhoto(fileName: String, onZoomChanged: (Boolean) -> Unit) {
    val handle = rememberStoredPhoto(fileName)
    var viewport by remember { mutableStateOf(IntSize.Zero) }
    var zoomPan by remember(fileName) { mutableStateOf(ZoomPan()) }
    LaunchedEffect(fileName, zoomPan.isZoomed) { onZoomChanged(zoomPan.isZoomed) }

    Box(
        modifier = Modifier.fillMaxSize().onSizeChanged { viewport = it },
        contentAlignment = Alignment.Center,
    ) {
        when (val photo = handle.photo) {
            PhotoLoad.Loading -> Unit
            PhotoLoad.Fetching -> CircularProgressIndicator(
                modifier = Modifier
                    .size(36.dp)
                    .semantics { contentDescription = "Downloading photo" },
                color = Color.White,
            )
            PhotoLoad.Unavailable -> IconButton(onClick = handle.retry) {
                Icon(
                    imageVector = Icons.Default.Refresh,
                    contentDescription = "Photo not available yet. Tap to retry",
                    tint = Color.White.copy(alpha = 0.55f),
                    modifier = Modifier.size(48.dp),
                )
            }
            // 0-18 A-2: 이 기기가 업로드 전에 바이트를 잃은 사진. 새로고침은 거짓 희망이다.
            PhotoLoad.Lost -> Column(horizontalAlignment = Alignment.CenterHorizontally) {
                Icon(
                    imageVector = Icons.Default.Warning,
                    contentDescription = null,
                    tint = Color.White.copy(alpha = 0.55f),
                    modifier = Modifier.size(48.dp),
                )
                Spacer(Modifier.height(12.dp))
                Text(
                    text = "This photo is gone from this device",
                    style = MaterialTheme.typography.bodyMedium,
                    color = Color.White.copy(alpha = 0.8f),
                )
                Text(
                    text = "It was never backed up, so it can't be recovered here.",
                    style = MaterialTheme.typography.bodySmall,
                    color = Color.White.copy(alpha = 0.55f),
                )
            }
            is PhotoLoad.Loaded -> {
                val fitted = remember(photo.value, viewport) {
                    fittedSize(photo.value.width, photo.value.height, viewport.width, viewport.height)
                }
                Image(
                    bitmap = photo.value,
                    contentDescription = fileName,
                    contentScale = ContentScale.Fit,
                    modifier = Modifier
                        .fillMaxSize()
                        .graphicsLayer {
                            scaleX = zoomPan.scale
                            scaleY = zoomPan.scale
                            translationX = zoomPan.offsetX
                            translationY = zoomPan.offsetY
                        }
                        // ── 왜 detectTransformGestures를 쓰지 않는가 ──────────────────
                        // 그쪽은 슬롭을 넘긴 드래그를 **전부 소비한다.** 1×에서도 소비하므로
                        // pager가 손짓을 아예 못 보고, `userScrollEnabled`를 켜 둬도 장이
                        // 넘어가지 않는다(실기기에서 확인). 그래서 직접 받아서 **손가락이
                        // 둘이거나 이미 확대된 경우에만** 소비한다 — 1× 한 손가락 드래그는
                        // 그대로 흘려보내 pager가 가져간다.
                        .pointerInput(fileName, fitted, viewport) {
                            awaitEachGesture {
                                awaitFirstDown(requireUnconsumed = false)
                                do {
                                    val event = awaitPointerEvent()
                                    val pressed = event.changes.count { it.pressed }
                                    val mine = pressed > 1 || zoomPan.isZoomed
                                    if (mine) {
                                        val zoom = event.calculateZoom()
                                        val pan = event.calculatePan()
                                        val centroid = event.calculateCentroid(useCurrent = false)
                                        var next = zoomPan
                                        if (zoom != 1f && centroid != Offset.Unspecified) {
                                            next = next.zoomedBy(
                                                factor = zoom,
                                                focusX = centroid.x - viewport.width / 2f,
                                                focusY = centroid.y - viewport.height / 2f,
                                                fitted = fitted,
                                                viewportWidth = viewport.width,
                                                viewportHeight = viewport.height,
                                            )
                                        }
                                        if (next.isZoomed) {
                                            next = next.pannedBy(pan.x, pan.y, fitted, viewport.width, viewport.height)
                                        }
                                        if (next != zoomPan) {
                                            zoomPan = next
                                            event.changes.forEach { it.consume() }
                                        }
                                    }
                                } while (event.changes.any { it.pressed })
                            }
                        }
                        .pointerInput(fileName, fitted, viewport) {
                            detectTapGestures(
                                onDoubleTap = { tap ->
                                    zoomPan = zoomPan.afterDoubleTap(
                                        focusX = tap.x - viewport.width / 2f,
                                        focusY = tap.y - viewport.height / 2f,
                                        fitted = fitted,
                                        viewportWidth = viewport.width,
                                        viewportHeight = viewport.height,
                                    )
                                },
                            )
                        },
                )
            }
        }
    }
}

/**
 * 닫기 · 몇 번째 장인지 · 오버플로. 자동 숨김은 두지 않는다 — 숨은 상태가 생기면 "지우기가
 * 없어졌다"는 오해가 뒤따르고, 지금 이 화면이 답해야 할 질문은 그게 아니다.
 */
@Composable
private fun ViewerChrome(
    position: String,
    canRotate: Boolean,
    rotating: Boolean,
    onClose: () -> Unit,
    onRotate: () -> Unit,
    onOpenActions: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().safeDrawingPadding().padding(horizontal = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        IconButton(onClick = onClose) {
            Icon(Icons.Default.Close, contentDescription = "Close", tint = Color.White)
        }
        Text(text = position, style = MaterialTheme.typography.labelLarge, color = Color.White)
        Row(verticalAlignment = Alignment.CenterVertically) {
            // ── 회전이 왜 시트가 아니라 상단 버튼인가 ──────────────────────────────
            // 누운 사진을 세우는 데 90°로는 모자랄 때가 있고(180°·270°), 시트 안에 두면
            // 한 번 돌릴 때마다 ⋮ → 항목 두 탭이 든다. 여기 두면 한 탭이고, 돌아간 결과가
            // 바로 뒤에 보인다.
            // 아이콘이 아니라 글자인 이유는 아이콘 집합이 core뿐이기 때문이다 — 여기에
            // 회전 아이콘이 없고, 있는 것 중 가장 가까운 Refresh는 이 화면에서 이미
            // "다시 받기"로 쓰고 있다.
            TextButton(onClick = onRotate, enabled = canRotate && !rotating) {
                if (rotating) {
                    CircularProgressIndicator(modifier = Modifier.size(18.dp), color = Color.White)
                } else {
                    Text(
                        text = "Rotate",
                        style = MaterialTheme.typography.labelLarge,
                        color = Color.White,
                    )
                }
            }
            IconButton(onClick = onOpenActions) {
                Icon(Icons.Default.MoreVert, contentDescription = "Photo options", tint = Color.White)
            }
        }
    }
}
